import { themes } from '../../../../assets';
import { getStatusInfo, STATUS_PRIMARY } from '../../../../utils';

const MARKER_SIZE = 46;
const MARKER_OUTLINE_COLOR = themes.yellow;

// Constantes do badge (label de pre-info acima do circulo). Largura e
// calculada dinamicamente medindo o texto pra acomodar conteudo variavel
// (parque so com UCHA vs parque misto UCHA+EBA, valor curto vs longo).
const BADGE_MARKER_HEIGHT = 76;
const BADGE_RECT_HEIGHT = 24;
const BADGE_RECT_Y = 4;
const BADGE_TAIL_HEIGHT = 6; // triangulo conectando badge ao circulo
const BADGE_TEXT_PADDING_X = 14; // padding horizontal interno do retangulo
const BADGE_OUTER_MARGIN = 8; // margem entre borda do retangulo e edge do SVG
const BADGE_FONT = '600 11px Arial, sans-serif';
const BADGE_MIN_RECT_WIDTH = 60; // largura minima pra balao nao ficar microscopico
const BADGE_MAX_RECT_WIDTH = 320; // ceiling de seguranca pra textos absurdos

// Canvas singleton pra medir texto via Canvas2D API. Browsers oferecem
// measureText() que retorna a largura em pixels do texto no font dado.
// Mais preciso que estimar por caractere (especialmente com emoji que
// tem largura variavel) e funciona offline (nao precisa estar no DOM).
let measureCanvas = null;
const measureBadgeTextWidth = (text) => {
  if (!text || typeof document === 'undefined') return 0;
  if (!measureCanvas) {
    measureCanvas = document.createElement('canvas');
  }
  const ctx = measureCanvas.getContext('2d');
  if (!ctx) return 0;
  ctx.font = BADGE_FONT;
  // measureText().width e fracionario — Math.ceil pra evitar truncar.
  return Math.ceil(ctx.measureText(text).width);
};

// Soma erros agregados do summary (ucha + eba) para badge de notificacao.
export const getErrorAmountFromSummary = (data = {}) =>
  Number(data?.ucha?.things?.range?.E || 0) +
  Number(data?.eba?.things?.range?.E || 0);

// Formata a contagem de equipamentos para caber no marker (max "99+").
export const getMarkerAmountLabel = (applianceAmount) => {
  const parsedAmount = Number(applianceAmount);
  if (!Number.isFinite(parsedAmount) || parsedAmount < 0) return '0';
  if (parsedAmount > 99) return '99+';
  return `${parsedAmount}`;
};

// Determina a cor de preenchimento do marker baseado no status do parque.
// Alarme/bloqueio = vermelho, rodando = verde-agua, atencao = laranja,
// desligada = cinza claro, sem dados = cinza escuro.
//
// Aceita 2 inputs pra retrocompat:
//   - string label legado (ex: "alarme/bloqueio/atenção") — vai pro
//     getStatusInfo que parseia e devolve struct
//   - struct info { primary, hasAttention } direto
//
// Internamente sempre opera no struct — zero string-match.
export const getMarkerFillColor = (statusOrInfo) => {
  if (!statusOrInfo) return themes.darkgray;
  // Normaliza pra info struct
  const info =
    typeof statusOrInfo === 'object' && 'primary' in statusOrInfo
      ? statusOrInfo
      : getStatusInfo(statusOrInfo);

  if (info.primary === STATUS_PRIMARY.ALARME) return themes.red;
  if (info.primary === STATUS_PRIMARY.RODANDO) {
    return info.hasAttention ? themes.orange : themes.watergreen;
  }
  if (info.primary === STATUS_PRIMARY.DESLIGADA) return themes.label;
  // SEM_DADOS (ou primary nao identificado, ex.: 'atenção' como label
  // standalone — caso vindo do statusBreakdown do MarkerDetailPanel)
  if (info.hasAttention) return themes.orange;
  return themes.darkgray;
};

// Formata um numero pro badge (PT-BR, max 1-2 decimais conforme magnitude).
// Exportado pra ser reusado no MarkerDetailPanel (mesmo padrao visual).
export const formatBadgeNumber = (value) => {
  if (!Number.isFinite(value)) return null;
  const abs = Math.abs(value);
  const maxFractionDigits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return value.toLocaleString('pt-BR', {
    maximumFractionDigits: maxFractionDigits,
  });
};

// Escala automatica pra kW -> MW quando faz sentido (>= 1000 kW). Mantem
// formatado em PT-BR. Evita "1234,5 kW" virar visualmente longo.
export const formatPowerForBadge = (kw) => {
  if (!Number.isFinite(kw)) return null;
  if (Math.abs(kw) >= 1000) {
    return `${formatBadgeNumber(kw / 1000)} MW`;
  }
  return `${formatBadgeNumber(kw)} kW`;
};

// Vazao em l/s. Escala pra m³/s quando >= 1000 l/s (1 m³/s = 1000 l/s).
export const formatFlowForBadge = (ls) => {
  if (!Number.isFinite(ls)) return null;
  if (Math.abs(ls) >= 1000) {
    return `${formatBadgeNumber(ls / 1000)} m³/s`;
  }
  return `${formatBadgeNumber(ls)} l/s`;
};

// Icone Lucide-style (line-icon X/Twitter) embutido inline no SVG do
// badge do marker. 24x24 viewBox, renderizado em 12x12 com scale 0.5.
// stroke=currentColor pra herdar a cor (texto branco) do <g> pai.
// Ver CLAUDE.md secao "Ícones" pra padrao do projeto.
const BADGE_ICON_PATHS = {
  // lucide:zap (potencia/energia)
  zap: 'M13 2L3 14h9l-1 8 10-12h-9l1-8z',
  // lucide:droplet (vazao/agua)
  droplet: 'M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0L12 2.69z',
};

// Setas de tendencia (delta vs valor anterior). Stroke verde pra up,
// vermelho pra down. flat = nao renderiza nada (poluicao visual).
// Paths em 24x24 — desenhados pra renderizar bonito em 10x10.
const TREND_ARROW_PATHS = {
  // lucide:arrow-up
  up: 'M12 19V5 M5 12l7-7 7 7',
  // lucide:arrow-down
  down: 'M12 5v14 M19 12l-7 7-7-7',
};
const TREND_ARROW_COLORS = {
  up: '#2ECC71', // verde claro
  down: '#E74C3C', // vermelho claro
};

// Calcula tendencia comparando current com previous. Threshold de 3% pra
// considerar 'flat' (evita seta dancando entre frames com ruido pequeno).
//   - up:    current > previous * 1.03
//   - down:  current < previous * 0.97
//   - flat:  diferenca menor que threshold
const computeTrend = (current, previous) => {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return 'flat';
  if (previous === 0) {
    return current > 0 ? 'up' : current < 0 ? 'down' : 'flat';
  }
  const ratio = current / previous;
  if (ratio > 1.03) return 'up';
  if (ratio < 0.97) return 'down';
  return 'flat';
};

// Segmentos do badge de pre-info do marker. Cada segmento e
// { icon: 'zap'|'droplet', value: 'X MW', trend: 'up'|'down'|'flat' }.
// Combinacao depende do tipo:
//   - UCHA (turbogerador): zap + potencia kW/MW
//   - EBA  (bomba):        droplet + vazao l/s ou m³/s
//   - Parque misto:        ambos
// previousRealtime: snapshot ~60s atras (mesmo formato de realtime) pra
// calcular trend. Pode ser null/undefined se nao tem historico ainda —
// nesse caso trend e 'flat' (sem seta no badge).
// Retorna array vazio se realtime ainda nao chegou ou todos zero.
// Cores dos badges por tipo: UCHA laranja, EBA azul (mesma familia dos
// tokens --cco-orange / --cco-blue e das fatias do cluster).
const UCHA_BADGE_COLOR = '#F6A560';
const EBA_BADGE_COLOR = '#4EA1F3';
export const buildMarkerBadgeSegments = (realtime, previousRealtime) => {
  if (!realtime || typeof realtime !== 'object') return [];
  const segments = [];

  // UCHA: geracao de energia em tempo real (potencia gerada, kW). O backend
  // manda negativo quando GERANDO (convencao do grid); exibimos o modulo
  // positivo, igual ao card do dashboard e ao pill do cluster. Icone zap +
  // cor laranja pra identificar a UCHA de relance (par do azul da EBA).
  const power = Number(realtime.onTimeUcha);
  if (Number.isFinite(power) && power !== 0) {
    const formatted = formatPowerForBadge(Math.abs(power));
    if (formatted) {
      const prevPower = Number(previousRealtime?.onTimeUcha);
      segments.push({
        icon: 'zap',
        color: UCHA_BADGE_COLOR,
        value: formatted,
        trend: computeTrend(Math.abs(power), Math.abs(prevPower)),
      });
    }
  }

  // EBA: potencia CONSUMIDA (kW) — antes mostrava vazao (l/s). O operador
  // pediu o consumo no balao. Icone de gota + cor azul pra identificar a
  // EBA (bomba/agua) de relance, diferenciando do balao da UCHA.
  const consumed = Number(realtime.potenciaEba);
  if (Number.isFinite(consumed) && consumed !== 0) {
    const formatted = formatPowerForBadge(consumed);
    if (formatted) {
      const prevConsumed = Number(previousRealtime?.potenciaEba);
      segments.push({
        icon: 'droplet',
        color: EBA_BADGE_COLOR,
        value: formatted,
        trend: computeTrend(consumed, prevConsumed),
      });
    }
  }

  return segments;
};

// Escape XML reservado pro texto entrar no SVG sem quebrar parse. Emoji
// unicode (⚡, 💧) passa direto — o data:URI vai encodificar pra utf8.
const escapeXmlText = (str) =>
  String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

// Constantes de layout do badge: cada segmento e
// [icone 12px][gap 4px][valor texto][gap 3px][trend 10px (opcional)].
// Entre segmentos colocamos um separador " · " (text puro, com 8px
// de respiro de cada lado).
const BADGE_ICON_SIZE = 12;
const BADGE_ICON_TEXT_GAP = 4;
const BADGE_TREND_SIZE = 10;
const BADGE_TREND_GAP = 3;
const BADGE_SEGMENT_SEPARATOR = '  ·  ';

// Mede a largura total do badge dado os segmentos. Soma:
//   - Por segmento: icone (12) + gap (4) + measureText(value) + trend opcional
//   - Entre segmentos: measureText(' · ')
const measureBadgeSegmentsWidth = (segments) => {
  if (!segments || !segments.length) return 0;
  let total = 0;
  segments.forEach((seg, i) => {
    if (i > 0) total += measureBadgeTextWidth(BADGE_SEGMENT_SEPARATOR);
    total += BADGE_ICON_SIZE + BADGE_ICON_TEXT_GAP;
    total += measureBadgeTextWidth(seg.value);
    if (seg.trend === 'up' || seg.trend === 'down') {
      total += BADGE_TREND_GAP + BADGE_TREND_SIZE;
    }
  });
  return Math.ceil(total);
};

// Constroi o icone SVG do marker com numero de equipamentos no centro,
// cor por status, shadow e (opcional) badge de pre-info acima.
// Retorna objeto compativel com google.maps Icon (url + size + anchor).
//
// badgeSegments: array opcional de { icon: 'zap'|'droplet', value: string }.
//   Cada segmento renderiza icone Lucide-style + valor. Separados por
//   ' · '. Vazio = sem badge, marker fica so com o circulo.
// selected: boolean opcional. Quando true, adiciona um anel branco
//   externo + circulo levemente maior + brilho — destaca visualmente
//   o marker do parque selecionado (cujo detalhe esta aberto no painel
//   lateral). Ajuda o operador a manter a conexao "qual parque estou
//   olhando".
// freshAlarm: boolean opcional. Quando true, adiciona um halo vermelho
//   pulsante extra ao redor do marker — sinaliza que o parque ENTROU
//   em alarme recentemente (<30s). Diferencia visualmente alarme novo
//   (atencao imediata necessaria) de alarme persistente (ja visto).
// Breakdown de status POR MAQUINA de um parque (a partir de
// appliance[].thing[].status). Usado pra pintar o circulo do marker como
// pizza quando o parque tem maquinas em estados diferentes (ex.: estacao
// com 4 bombas, 2 rodando + 2 desligadas) — antes o circulo era 1 cor so
// (status agregado) e escondia essa composicao.
export const getParkStatusCounts = (parque) => {
  const counts = {
    'alarme/bloqueio': 0,
    atenção: 0,
    rodando: 0,
    desligada: 0,
    'sem dados': 0,
  };
  let total = 0;
  ((parque && parque.appliance) || []).forEach((app) => {
    ((app && app.thing) || []).forEach((t) => {
      const st = t && t.status;
      const name = st && (st.name != null ? st.name : st);
      counts[bucketizeStatus(name)] += 1;
      total += 1;
    });
  });
  return { counts, total };
};

// Serializa o breakdown pra chave de cache do icone (ordem fixa).
export const statusCountsKey = (statusCounts) => {
  if (!statusCounts || !statusCounts.counts) return '';
  const c = statusCounts.counts;
  return `${c['alarme/bloqueio'] || 0}.${c['atenção'] || 0}.${
    c.rodando || 0
  }.${c.desligada || 0}.${c['sem dados'] || 0}`;
};

export const buildMarkerIcon = (
  google,
  status,
  applianceAmount,
  badgeSegments = [],
  selected = false,
  freshAlarm = false,
  pendingCommand = false,
  statusCounts = null
) => {
  if (!google || !google.maps) return undefined;

  const amountLabel = getMarkerAmountLabel(applianceAmount);
  const fillColor = getMarkerFillColor(status);
  const fontSize =
    amountLabel.length > 2 ? 11 : amountLabel.length > 1 ? 14 : 16;

  const hasBadge = !!(badgeSegments && badgeSegments.length);

  // Quando selecionado OU em alarme novo, o halo pulsante chega ate
  // r=30/32. Crescemos o SVG simetricamente pra acomodar sem clipping.
  // circleY/circleX sao ajustados na mesma medida pro anchor continuar
  // no centro do marker — posicao geo nao muda quando halo aparece.
  let SELECTED_PAD = 0;
  if (freshAlarm) SELECTED_PAD = Math.max(SELECTED_PAD, 16);
  if (selected) SELECTED_PAD = Math.max(SELECTED_PAD, 12);
  // Parque em ATENCAO (warning): anel laranja sutil pulsante, distinto do
  // halo vermelho de alarme novo. So quando nao ha halo maior (selecionado
  // ou alarme novo) pra nao competir visualmente.
  const hasAttention =
    typeof status === 'string' && status.toLowerCase().includes('aten');
  const showAttentionRing = hasAttention && !selected && !freshAlarm;
  if (showAttentionRing) SELECTED_PAD = Math.max(SELECTED_PAD, 10);

  // Tamanho proporcional ao nº de equipamentos do parque (subtil): mais
  // maquinas -> circulo levemente maior. Escala log, cap 1.3x. sizePad
  // cresce o SVG pra o circulo maior nao clipar (anchor segue no centro).
  const markerScale = Math.min(
    1.3,
    1 + Math.log10(Math.max(1, Number(applianceAmount) || 1)) * 0.22,
  );
  const sizePad = Math.round((markerScale - 1) * 20);
  if (sizePad > 0) SELECTED_PAD = Math.max(SELECTED_PAD, sizePad);

  // Largura dinamica: mede icones + texto + separadores via canvas e
  // adiciona padding. Clamp entre min/max pra cobrir parques muito
  // curtos e proteger contra conteudo patologicamente longo.
  let rectWidth = 0;
  let baseWidth = MARKER_SIZE;
  if (hasBadge) {
    const contentW = measureBadgeSegmentsWidth(badgeSegments);
    rectWidth = Math.min(
      BADGE_MAX_RECT_WIDTH,
      Math.max(BADGE_MIN_RECT_WIDTH, contentW + BADGE_TEXT_PADDING_X * 2),
    );
    // Garante largura minima do SVG pra acomodar o circulo (46px) caso
    // o balao seja mais estreito que ele (parque muito pequeno).
    baseWidth = Math.max(MARKER_SIZE, rectWidth + BADGE_OUTER_MARGIN * 2);
  }
  const width = baseWidth + SELECTED_PAD * 2;
  const baseHeight = hasBadge ? BADGE_MARKER_HEIGHT : MARKER_SIZE;
  const height = baseHeight + SELECTED_PAD * 2;
  // Posicao do circulo dentro do SVG. Sem badge: centro do icone.
  // Com badge: deslocado pra baixo pra abrir espaco no topo.
  // +SELECTED_PAD desloca pra dentro do novo padding extra.
  const circleX = width / 2;
  const circleY = hasBadge
    ? BADGE_RECT_Y + BADGE_RECT_HEIGHT + BADGE_TAIL_HEIGHT + 16 + SELECTED_PAD
    : 23 + SELECTED_PAD;

  let badgeSvg = '';
  if (hasBadge) {
    // Centraliza o retangulo horizontalmente. circleX e o centro do SVG.
    // badgeRectY desloca o badge dentro do padding extra quando
    // selecionado pra continuar logo acima do circulo.
    const rectX = circleX - rectWidth / 2;
    const badgeRectY = BADGE_RECT_Y + SELECTED_PAD;
    const textBaselineY = badgeRectY + BADGE_RECT_HEIGHT / 2 + 4;
    const iconY = badgeRectY + (BADGE_RECT_HEIGHT - BADGE_ICON_SIZE) / 2;
    // Triangulo apontando do centro inferior do retangulo pro topo do
    // circulo — visualmente conecta badge e marker.
    const tailTopY = badgeRectY + BADGE_RECT_HEIGHT;
    const tailBottomY = tailTopY + BADGE_TAIL_HEIGHT;

    // Calcula o ponto de partida horizontal pra centralizar o conjunto
    // [icone + texto + ... + icone + texto] dentro do retangulo. Sem
    // isso, conteudos curtos ficariam encostados na esquerda.
    const contentW = measureBadgeSegmentsWidth(badgeSegments);
    let cursorX = rectX + (rectWidth - contentW) / 2;

    // Render por segmento: <g translate+scale> com o path do icone +
    // <text> ao lado. Entre segmentos, separador " · ".
    const segmentsSvg = badgeSegments
      .map((seg, i) => {
        let prefix = '';
        if (i > 0) {
          // Separador " · " entre segmentos
          const sepW = measureBadgeTextWidth(BADGE_SEGMENT_SEPARATOR);
          const sepX = cursorX + sepW / 2;
          prefix = `<text x="${sepX}" y="${textBaselineY}" fill="${themes.white}" font-size="11" font-weight="600" font-family="Arial, sans-serif" text-anchor="middle" opacity="0.6">·</text>`;
          cursorX += sepW;
        }
        const iconX = cursorX;
        // Icone Lucide-style (24x24 viewBox -> scale 0.5 = 12x12) com
        // stroke=currentColor branco do <g> pai pra herdar o tema.
        const iconPath = BADGE_ICON_PATHS[seg.icon] || '';
        // Cor por segmento (ex.: EBA azul). Default branco (UCHA/legado).
        const segColor = seg.color || themes.white;
        const iconSvg = `<g transform="translate(${iconX}, ${iconY}) scale(0.5)" fill="none" stroke="${segColor}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="${iconPath}"/></g>`;
        cursorX += BADGE_ICON_SIZE + BADGE_ICON_TEXT_GAP;
        const textW = measureBadgeTextWidth(seg.value);
        const textX = cursorX;
        const safeValue = escapeXmlText(seg.value);
        const textSvg = `<text x="${textX}" y="${textBaselineY}" fill="${segColor}" font-size="11" font-weight="600" font-family="Arial, sans-serif" text-anchor="start">${safeValue}</text>`;
        cursorX += textW;

        // Trend arrow (up/down) opcional apos o texto. Cor verde pra
        // up, vermelho pra down. flat = nao renderiza nada (limpo).
        let trendSvg = '';
        if (seg.trend === 'up' || seg.trend === 'down') {
          cursorX += BADGE_TREND_GAP;
          const trendY =
            badgeRectY + (BADGE_RECT_HEIGHT - BADGE_TREND_SIZE) / 2;
          const trendPath = TREND_ARROW_PATHS[seg.trend];
          const trendColor = TREND_ARROW_COLORS[seg.trend];
          // scale 24->10 = 10/24 ~= 0.417
          trendSvg = `<g transform="translate(${cursorX}, ${trendY}) scale(0.417)" fill="none" stroke="${trendColor}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="${trendPath}"/></g>`;
          cursorX += BADGE_TREND_SIZE;
        }
        return prefix + iconSvg + textSvg + trendSvg;
      })
      .join('');

    badgeSvg = `
      <g filter="url(#marker-shadow)">
        <rect x="${rectX}" y="${badgeRectY}" width="${rectWidth}" height="${BADGE_RECT_HEIGHT}" rx="12" fill="#1f1f1f" stroke="#5a5a5a" stroke-width="1"/>
        <polygon points="${circleX - 5},${tailTopY} ${circleX + 5},${tailTopY} ${circleX},${tailBottomY}" fill="#1f1f1f" stroke="#5a5a5a" stroke-width="1"/>
        ${segmentsSvg}
      </g>
    `;
  }

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
      <defs>
        <filter id="marker-shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.35"/>
        </filter>
      </defs>
      ${badgeSvg}
      <g filter="url(#marker-shadow)">
        ${
          showAttentionRing
            ? `
              <!-- Anel laranja sutil pulsante: parque em ATENCAO (warning).
                   Mais discreto que o halo de alarme novo (vermelho), so
                   pra puxar o olhar sem alarmar. -->
              <circle cx="${circleX}" cy="${circleY}" r="22" fill="none" stroke="${themes.orange}" stroke-width="2" opacity="0.55">
                <animate attributeName="r" values="19;25;19" dur="1.8s" repeatCount="indefinite"/>
                <animate attributeName="opacity" values="0.6;0.15;0.6" dur="1.8s" repeatCount="indefinite"/>
              </circle>
            `
            : ''
        }
        ${
          freshAlarm
            ? `
              <!-- Halo vermelho pulsante extra: marker ENTROU em alarme
                   recentemente (<30s). Diferencia visualmente alarme
                   novo de alarme persistente. Animacao SVG <animate>
                   funciona em FF; em Chrome fica estatico mas o anel
                   maior + cor vermelha ja chamam atencao. -->
              <circle cx="${circleX}" cy="${circleY}" r="28" fill="${themes.red}" opacity="0.25">
                <animate attributeName="r" values="22;32;22" dur="1.2s" repeatCount="indefinite"/>
                <animate attributeName="opacity" values="0.35;0.0;0.35" dur="1.2s" repeatCount="indefinite"/>
              </circle>
              <circle cx="${circleX}" cy="${circleY}" r="24" fill="none" stroke="${themes.red}" stroke-width="2.5" opacity="0.9"/>
            `
            : ''
        }
        ${
          selected
            ? `
              <!-- Halo azul claro animado pulsando (sinaliza selecao
                   forte mesmo quando o operador olha de longe na TV).
                   SVG <animate> em data:URI funciona no Chrome/Edge/FF
                   modernos. Fallback grace: se nao animar, ainda mostra
                   o anel maior estatico. -->
              <circle cx="${circleX}" cy="${circleY}" r="26" fill="none" stroke="${themes.lightblue}" stroke-width="2.5" opacity="0.6">
                <animate attributeName="r" values="22;30;22" dur="1.6s" repeatCount="indefinite"/>
                <animate attributeName="opacity" values="0.7;0.1;0.7" dur="1.6s" repeatCount="indefinite"/>
              </circle>
              <!-- Anel branco fixo intermediario, define contorno claro -->
              <circle cx="${circleX}" cy="${circleY}" r="22" fill="none" stroke="${themes.white}" stroke-width="3" opacity="0.95"/>
            `
            : ''
        }
        <circle cx="${circleX}" cy="${circleY}" r="${(selected ? 19 : 16) * markerScale}" fill="none" stroke="${selected ? themes.lightblue : MARKER_OUTLINE_COLOR}" stroke-width="${selected ? 5 : 4}"/>
        ${(() => {
          // Miolo: pizza por status QUANDO o parque tem >=2 maquinas em
          // estados diferentes (ex.: 2 bombas rodando + 2 desligadas). Senao
          // circulo solido (status agregado) — comportamento original.
          const rIn = (selected ? 15 : 12.5) * markerScale;
          const total = statusCounts && statusCounts.total;
          const counts = (statusCounts && statusCounts.counts) || null;
          const order = [
            'alarme/bloqueio',
            'atenção',
            'rodando',
            'desligada',
            'sem dados',
          ];
          const nonZero = counts
            ? order.filter((b) => (counts[b] || 0) > 0)
            : [];
          if (!counts || !total || total < 2 || nonZero.length < 2) {
            return `<circle cx="${circleX}" cy="${circleY}" r="${rIn}" fill="${fillColor}"/>`;
          }
          let cursor = -Math.PI / 2; // comeca no topo (12h)
          let slices = '';
          order.forEach((bucket) => {
            const n = counts[bucket] || 0;
            if (!n) return;
            const end = cursor + (n / total) * Math.PI * 2;
            const d = buildPieSliceD(circleX, circleY, rIn, cursor, end);
            slices += `<path d="${d}" fill="${getMarkerFillColor(bucket)}"/>`;
            cursor = end;
          });
          return slices;
        })()}
        <text x="${circleX}" y="${circleY}" fill="${themes.white}" font-size="${Math.round(fontSize * markerScale)}" font-weight="700" font-family="Arial, sans-serif" text-anchor="middle" dominant-baseline="central" style="paint-order:stroke" stroke="rgba(0,0,0,0.45)" stroke-width="2.5">${amountLabel}</text>
        ${
          pendingCommand
            ? (() => {
                // Indicador "comando aguardando" (shadow desired pendente):
                // badge ciano com um relogio, no canto inferior-direito.
                const rOut = (selected ? 19 : 16) * markerScale;
                const bx = circleX + rOut * 0.72;
                const by = circleY + rOut * 0.72;
                const br = 7 * markerScale;
                const h1 = br * 0.45;
                const h2 = br * 0.4;
                return `
                  <circle cx="${bx}" cy="${by}" r="${br}" fill="#22D3EE" stroke="#0f1117" stroke-width="1.5"/>
                  <path d="M${bx} ${by - h1} L${bx} ${by} L${bx + h2} ${by}" fill="none" stroke="#0f1117" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
                `;
              })()
            : ''
        }
      </g>
    </svg>
  `;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(width, height),
    anchor: new google.maps.Point(circleX, circleY),
  };
};

// Cache memoizado das chamadas a buildMarkerIcon. Sem isso, cada render
// do PracasMapa criava um objeto Icon novo (mesma URL/size/anchor mas
// referencia nova), e google-maps-react detecta como mudanca de prop
// e re-cria o marker no mapa — provocando flicker visivel em cada
// polling tick, mesmo quando NADA mudou.
//
// Com cache: mesma combinacao (status, applianceAmount, badgeText,
// selected) -> mesma referencia de objeto -> google-maps-react no-op,
// sem flicker. Cache size limitado pra evitar crescimento indefinido
// (rotacao FIFO simples — N parques * variacao de badgeText fica
// confortavelmente abaixo do limite).
const iconCache = new Map();
const ICON_CACHE_MAX_SIZE = 500;

export const getCachedMarkerIcon = (
  google,
  status,
  applianceAmount,
  badgeSegments,
  selected,
  freshAlarm = false,
  pendingCommand = false,
  statusCounts = null
) => {
  if (!google || !google.maps) return undefined;
  // Serializa segmentos pra chave de cache. Cada segmento e icon+value
  // — combinacoes diferentes geram chaves diferentes -> ate cached
  // separadamente.
  const segmentsKey = (badgeSegments || [])
    .map((s) => `${s.icon}:${s.color || ''}:${s.value}`)
    .join(',');
  // O breakdown por status entra na chave: parque que muda de "2R+2D" pra
  // "3R+1D" gera icone novo (pizza diferente) sem colidir no cache.
  const key = `${status}|${applianceAmount}|${segmentsKey}|${selected ? 1 : 0}|${freshAlarm ? 1 : 0}|${pendingCommand ? 1 : 0}|${statusCountsKey(statusCounts)}`;
  const cached = iconCache.get(key);
  if (cached) return cached;
  const icon = buildMarkerIcon(
    google,
    status,
    applianceAmount,
    badgeSegments,
    selected,
    freshAlarm,
    pendingCommand,
    statusCounts,
  );
  if (iconCache.size >= ICON_CACHE_MAX_SIZE) {
    // FIFO simples: descarta a entrada mais antiga (primeira inserida).
    const firstKey = iconCache.keys().next().value;
    iconCache.delete(firstKey);
  }
  iconCache.set(key, icon);
  return icon;
};

// ============================================================================
// Badge "pill" STANDALONE (marker separado) — anti-colisao
// ============================================================================
// O balao de pre-info (geracao UCHA / potencia EBA) virou um MARKER PROPRIO,
// separado do circulo, pra poder ser reposicionado (cima/baixo/esq/dir) e
// evitar sobreposicao com outros markers/clusters (placement greedy em
// pracas-mapa). Aqui so desenhamos o pill + a setinha (tail) apontando pro
// centro do circulo, e calculamos a ancora pra o pill "flutuar" do lado
// escolhido com folga do circulo.

const PILL_CLEAR = 16; // folga entre a ponta do tail e o centro do circulo
const PILL_PAD = 5; // margem interna pro shadow nao clipar
// rectH = BADGE_RECT_HEIGHT (24), tail = BADGE_TAIL_HEIGHT (6)

// Largura do retangulo do pill (mesma logica do badge in-icon). Exportada
// pra pracas-mapa medir o rect na deteccao de colisao com o MESMO valor.
export const measureBadgePillWidth = (segments) => {
  if (!segments || !segments.length) return 0;
  const contentW = measureBadgeSegmentsWidth(segments);
  return Math.min(
    BADGE_MAX_RECT_WIDTH,
    Math.max(BADGE_MIN_RECT_WIDTH, contentW + BADGE_TEXT_PADDING_X * 2),
  );
};

// Altura extra do pill quando tem sparkline (faixa da mini-curva abaixo
// do valor). Exportado pra colisao e desenho usarem a MESMA altura.
const PILL_SPARK_EXTRA = 11;
export const badgePillHeight = (hasSpark) =>
  BADGE_RECT_HEIGHT + (hasSpark ? PILL_SPARK_EXTRA : 0);

// Retangulo do pill em coordenadas de TELA (container px), dado o centro do
// circulo (cx, cy), o lado (placement) e a altura do rect. Usado na deteccao
// de colisao. A geometria casa 1:1 com o que buildBadgePillIcon desenha.
export const badgePillRect = (placement, cx, cy, rectW, rectH = BADGE_RECT_HEIGHT) => {
  const off = PILL_CLEAR + BADGE_TAIL_HEIGHT; // distancia centro -> borda do rect
  switch (placement) {
    case 'bottom':
      return { x: cx - rectW / 2, y: cy + off, w: rectW, h: rectH };
    case 'right':
      return { x: cx + off, y: cy - rectH / 2, w: rectW, h: rectH };
    case 'left':
      return { x: cx - off - rectW, y: cy - rectH / 2, w: rectW, h: rectH };
    case 'top':
    default:
      return { x: cx - rectW / 2, y: cy - off - rectH, w: rectW, h: rectH };
  }
};

// Renderiza os segmentos (icone + valor + trend) dentro do rect do pill.
// Espelha o loop do buildMarkerIcon, mas standalone pra nao mexer naquele.
const renderPillSegments = (badgeSegments, rectX, rectWidth, rectY) => {
  const textBaselineY = rectY + BADGE_RECT_HEIGHT / 2 + 4;
  const iconY = rectY + (BADGE_RECT_HEIGHT - BADGE_ICON_SIZE) / 2;
  const contentW = measureBadgeSegmentsWidth(badgeSegments);
  let cursorX = rectX + (rectWidth - contentW) / 2;
  return badgeSegments
    .map((seg, i) => {
      let prefix = '';
      if (i > 0) {
        const sepW = measureBadgeTextWidth(BADGE_SEGMENT_SEPARATOR);
        const sepX = cursorX + sepW / 2;
        prefix = `<text x="${sepX}" y="${textBaselineY}" fill="${themes.white}" font-size="11" font-weight="600" font-family="Arial, sans-serif" text-anchor="middle" opacity="0.6">·</text>`;
        cursorX += sepW;
      }
      const iconX = cursorX;
      const iconPath = BADGE_ICON_PATHS[seg.icon] || '';
      const segColor = seg.color || themes.white;
      const iconSvg = `<g transform="translate(${iconX}, ${iconY}) scale(0.5)" fill="none" stroke="${segColor}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="${iconPath}"/></g>`;
      cursorX += BADGE_ICON_SIZE + BADGE_ICON_TEXT_GAP;
      const textW = measureBadgeTextWidth(seg.value);
      const textX = cursorX;
      const safeValue = escapeXmlText(seg.value);
      const textSvg = `<text x="${textX}" y="${textBaselineY}" fill="${segColor}" font-size="11" font-weight="600" font-family="Arial, sans-serif" text-anchor="start">${safeValue}</text>`;
      cursorX += textW;
      let trendSvg = '';
      if (seg.trend === 'up' || seg.trend === 'down') {
        cursorX += BADGE_TREND_GAP;
        const trendY = rectY + (BADGE_RECT_HEIGHT - BADGE_TREND_SIZE) / 2;
        const trendPath = TREND_ARROW_PATHS[seg.trend];
        const trendColor = TREND_ARROW_COLORS[seg.trend];
        trendSvg = `<g transform="translate(${cursorX}, ${trendY}) scale(0.417)" fill="none" stroke="${trendColor}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="${trendPath}"/></g>`;
        cursorX += BADGE_TREND_SIZE;
      }
      return prefix + iconSvg + textSvg + trendSvg;
    })
    .join('');
};

// Mini-sparkline: polyline dos ultimos valores (gen+consumo) numa faixa
// abaixo do valor. Mostra a tendencia recente alem da setinha up/down.
const renderPillSpark = (spark, rectX, rectW, rectY, rectH) => {
  const n = spark.length;
  if (n < 2) return '';
  const min = Math.min.apply(null, spark);
  const max = Math.max.apply(null, spark);
  const span = max - min || 1;
  const x0 = rectX + 10;
  const x1 = rectX + rectW - 10;
  const yTop = rectY + BADGE_RECT_HEIGHT - 2; // logo abaixo do texto
  const yBot = rectY + rectH - 4;
  const pts = spark
    .map((v, i) => {
      const x = x0 + ((x1 - x0) * i) / (n - 1);
      const t = (v - min) / span;
      const y = yBot - t * (yBot - yTop);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return `<polyline points="${pts}" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>`;
};

// Constroi o icone do pill standalone pro lado pedido. Retorna objeto
// google.maps Icon (url + scaledSize + anchor). A ancora e o ponto do SVG
// que cai sobre a latlng (= centro do circulo), de forma que o pill fique
// deslocado pro lado com folga (PILL_CLEAR) e o tail aponte pro circulo.
// opts: { stale?: bool (esmaece quando o stream tempo-real caiu),
//         spark?: number[] (mini-curva da tendencia recente) }.
export const buildBadgePillIcon = (
  google,
  badgeSegments,
  placement = 'top',
  opts = {},
) => {
  if (!google || !google.maps || !badgeSegments || !badgeSegments.length) {
    return undefined;
  }
  const { stale = false, spark = null } = opts;
  const hasSpark = Array.isArray(spark) && spark.length >= 3;
  const rectW = measureBadgePillWidth(badgeSegments);
  const rectH = badgePillHeight(hasSpark);
  const tail = BADGE_TAIL_HEIGHT;
  const pad = PILL_PAD;
  const clear = PILL_CLEAR;

  let W;
  let H;
  let rectX;
  let rectY;
  let anchorX;
  let anchorY;
  let tailPts;

  if (placement === 'bottom') {
    W = rectW + pad * 2;
    rectX = pad;
    anchorX = W / 2;
    anchorY = pad;
    const tipY = anchorY + clear;
    rectY = tipY + tail;
    H = rectY + rectH + pad;
    const cx = W / 2;
    tailPts = `${cx},${tipY} ${cx - 5},${tipY + tail} ${cx + 5},${tipY + tail}`;
  } else if (placement === 'right') {
    H = rectH + pad * 2;
    rectY = pad;
    anchorX = pad;
    anchorY = H / 2;
    const tipX = anchorX + clear;
    rectX = tipX + tail;
    W = rectX + rectW + pad;
    const cy = H / 2;
    tailPts = `${tipX},${cy} ${tipX + tail},${cy - 5} ${tipX + tail},${cy + 5}`;
  } else if (placement === 'left') {
    H = rectH + pad * 2;
    rectY = pad;
    W = pad + rectW + tail + clear + pad;
    anchorX = W - pad;
    anchorY = H / 2;
    const tipX = anchorX - clear;
    rectX = tipX - tail - rectW;
    const cy = H / 2;
    tailPts = `${tipX},${cy} ${tipX - tail},${cy - 5} ${tipX - tail},${cy + 5}`;
  } else {
    // top (default)
    W = rectW + pad * 2;
    rectX = pad;
    rectY = pad;
    anchorX = W / 2;
    anchorY = pad + rectH + tail + clear;
    H = anchorY;
    const cx = W / 2;
    const tipY = rectY + rectH + tail;
    tailPts = `${cx},${tipY} ${cx - 5},${rectY + rectH} ${cx + 5},${rectY + rectH}`;
  }

  const segmentsSvg = renderPillSegments(badgeSegments, rectX, rectW, rectY);
  const sparkSvg = hasSpark
    ? renderPillSpark(spark, rectX, rectW, rectY, rectH)
    : '';

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
      <defs>
        <filter id="pill-shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.35"/>
        </filter>
      </defs>
      <g filter="url(#pill-shadow)"${stale ? ' opacity="0.45"' : ''}>
        <polygon points="${tailPts}" fill="#1f1f1f" stroke="#5a5a5a" stroke-width="1"/>
        <rect x="${rectX}" y="${rectY}" width="${rectW}" height="${rectH}" rx="${BADGE_RECT_HEIGHT / 2}" fill="#1f1f1f" stroke="#5a5a5a" stroke-width="1"/>
        ${segmentsSvg}
        ${sparkSvg}
      </g>
    </svg>
  `;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(W, H),
    anchor: new google.maps.Point(anchorX, anchorY),
  };
};

// Cache dos pills standalone (memoiza por segmentos + placement). Sem isso,
// recriava objeto Icon a cada placement/tick -> setIcon detecta mudanca e
// re-cria o marker no Google Maps (flicker).
const pillIconCache = new Map();
const PILL_CACHE_MAX_SIZE = 400;
export const getCachedBadgePillIcon = (
  google,
  badgeSegments,
  placement,
  opts = {},
) => {
  if (!google || !google.maps || !badgeSegments || !badgeSegments.length) {
    return undefined;
  }
  const { stale = false, spark = null } = opts;
  const segmentsKey = badgeSegments
    .map((s) => `${s.icon}:${s.color || ''}:${s.value}:${s.trend || ''}`)
    .join(',');
  // Spark entra na chave arredondado (a curva muda -> icone novo). Bucketiza
  // pra nao explodir o cache com micro-variacoes.
  const sparkKey =
    Array.isArray(spark) && spark.length >= 3
      ? spark.map((v) => Math.round(v / 5)).join('.')
      : '';
  const key = `${placement}|${stale ? 's' : ''}|${segmentsKey}|${sparkKey}`;
  const cached = pillIconCache.get(key);
  if (cached) return cached;
  const icon = buildBadgePillIcon(google, badgeSegments, placement, {
    stale,
    spark,
  });
  if (pillIconCache.size >= PILL_CACHE_MAX_SIZE) {
    const firstKey = pillIconCache.keys().next().value;
    pillIconCache.delete(firstKey);
  }
  pillIconCache.set(key, icon);
  return icon;
};

// ============================================================================
// Cluster icons (MarkerClusterer)
// ============================================================================

// Prioridade pra escolher o pior status entre os parques agrupados num
// cluster. O cluster recebe a cor do status mais grave dos seus filhos —
// ex.: se um dos parques esta em alarme, o cluster fica vermelho. Isso
// garante que o operador na TV nao perde alertas escondidos atras de
// agrupamento.
const STATUS_PRIORITY = {
  'alarme/bloqueio': 5,
  'alarme/bloqueio/atenção': 5,
  atenção: 4,
  'rodando/atenção': 3,
  'desligada/atenção': 3,
  'sem dados/atenção': 3,
  rodando: 2,
  desligada: 1,
  'sem dados': 0,
};

export const pickWorstStatus = (statuses) => {
  if (!statuses || !statuses.length) return 'sem dados';
  let worst = statuses[0];
  for (let i = 1; i < statuses.length; i += 1) {
    const candidate = statuses[i];
    if ((STATUS_PRIORITY[candidate] || 0) > (STATUS_PRIORITY[worst] || 0)) {
      worst = candidate;
    }
  }
  return worst;
};

// Conta parques por bucket de status. Usado pra renderizar o pizza
// chart do cluster. Opera via getStatusInfo (struct) em vez de .includes
// no label — alarme tem precedencia sobre atencao, atencao sobre rodando.
const bucketizeStatus = (label) => {
  if (!label) return 'sem dados';
  const info = getStatusInfo(label);
  if (info.primary === STATUS_PRIMARY.ALARME) return 'alarme/bloqueio';
  if (info.hasAttention) return 'atenção';
  if (info.primary === STATUS_PRIMARY.RODANDO) return 'rodando';
  if (info.primary === STATUS_PRIMARY.DESLIGADA) return 'desligada';
  return 'sem dados';
};

export const countStatusesInCluster = (statusLabels) => {
  const buckets = {
    'alarme/bloqueio': 0,
    atenção: 0,
    rodando: 0,
    desligada: 0,
    'sem dados': 0,
  };
  (statusLabels || []).forEach((label) => {
    buckets[bucketizeStatus(label)] += 1;
  });
  return buckets;
};

// Agrega um cluster em MAQUINAS (nao parques): o numero grande do cluster
// soma o applianceAmount de cada parque agrupado — consistente com o
// marker individual, que mostra o nº de maquinas do parque. (Antes o
// cluster mostrava o nº de PARQUES: "3+4 maquinas" agrupava como "2", e o
// operador lia como soma errada.) O pie tambem e ponderado por maquinas,
// pra fatia bater com o numero exibido. Cada marker nativo carrega
// statusLabel e applianceAmount via marker.set (ver pracas-mapa).
export const aggregateClusterMachines = (clusterMarkers) => {
  const counts = {
    'alarme/bloqueio': 0,
    atenção: 0,
    rodando: 0,
    desligada: 0,
    'sem dados': 0,
  };
  let totalMachines = 0;
  (clusterMarkers || []).forEach((m) => {
    if (!m || !m.get) return;
    // Fallback 1: parque sempre tem >=1 maquina (markerHasEquipment filtra
    // os zerados) — protege markers antigos sem a propriedade setada.
    const machines = Number(m.get('applianceAmount')) || 1;
    counts[bucketizeStatus(m.get('statusLabel'))] += machines;
    totalMachines += machines;
  });
  return { counts, totalMachines };
};

// Helper de arco SVG. Dado um centro, raio e angulos inicial/final em
// radianos, retorna o atributo 'd' de um <path> que desenha um setor
// de pizza (do centro pro arco e volta).
const buildPieSliceD = (cx, cy, r, startAngle, endAngle) => {
  const startX = cx + r * Math.cos(startAngle);
  const startY = cy + r * Math.sin(startAngle);
  const endX = cx + r * Math.cos(endAngle);
  const endY = cy + r * Math.sin(endAngle);
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  return `M ${cx} ${cy} L ${startX} ${startY} A ${r} ${r} 0 ${largeArc} 1 ${endX} ${endY} Z`;
};

// Icone do cluster — pizza chart com proporcao de cada status + count
// no centro. Antes o cluster recebia so a cor do pior status, escondendo
// a composicao. Agora o operador ve "5 verdes + 2 vermelhos + 1 amarelo"
// num olhar — ganho enorme de info visual em zoom afastado.
// Cores por tipo (convencao do app): UCHA/geracao laranja, EBA/consumo azul.
const CLUSTER_GEN_COLOR = '#F6A560';
const CLUSTER_CONS_COLOR = '#3FA9F5';

// sums (opcional) = { gen, cons } em kW — soma tempo-real dos parques
// RODANDO do cluster (geracao UCHA / consumo EBA). Renderiza um pill
// abaixo do circulo pra visualizar o total mesmo agrupado (zoom out).
export const buildClusterIcon = (google, counts, total, sums) => {
  if (!google || !google.maps) return undefined;
  const label = total > 99 ? '99+' : `${total}`;
  // Tamanho proporcional ao nº de parques (escala log, cap 1.5x) — cluster
  // maior "pesa" mais no relance. fontSize acompanha a escala.
  const sizeScale = Math.min(1.5, 1 + Math.log10(Math.max(1, total)) * 0.32);
  const fontSizeBase = label.length > 2 ? 14 : label.length > 1 ? 17 : 19;
  const fontSize = Math.round(fontSizeBase * sizeScale);

  // Pills de soma (so RODANDO; valores ja agregados pelo renderer).
  const pillSegs = [];
  if (sums && Number.isFinite(sums.gen) && sums.gen !== 0) {
    const v = formatPowerForBadge(sums.gen);
    if (v) pillSegs.push({ icon: 'zap', value: v, color: CLUSTER_GEN_COLOR });
  }
  if (sums && Number.isFinite(sums.cons) && sums.cons !== 0) {
    const v = formatPowerForBadge(sums.cons);
    if (v)
      pillSegs.push({ icon: 'droplet', value: v, color: CLUSTER_CONS_COLOR });
  }
  const hasPill = pillSegs.length > 0;
  const r = Math.round(22 * sizeScale);
  const clusterSize = r * 2 + 12; // diametro + margem (borda/sombra)
  // Reusa o layout do pill standalone (icone + valor por segmento). A
  // largura vem do medidor compartilhado (icone 12 + gap + texto +
  // separador), garantindo centralizacao correta com os icones.
  const pillH = BADGE_RECT_HEIGHT;
  const pillContentW = hasPill ? measureBadgeSegmentsWidth(pillSegs) : 0;
  const pillW = hasPill
    ? Math.max(clusterSize, pillContentW + 16)
    : clusterSize;
  const W = Math.max(clusterSize, pillW);
  const cxC = W / 2;
  const pillGap = 4;
  const H = hasPill ? clusterSize + pillGap + pillH : clusterSize;

  // Ordem fixa pra render — alarme primeiro pra fatia vermelha ficar
  // visualmente no topo (12h) se for o primeiro slice.
  const order = ['alarme/bloqueio', 'atenção', 'rodando', 'desligada', 'sem dados'];
  const cx = cxC;
  const cy = clusterSize / 2;

  let pieSvg = '';
  if (total > 0) {
    let cursorAngle = -Math.PI / 2; // comeca no topo (12h)
    order.forEach((bucket) => {
      const n = counts[bucket] || 0;
      if (!n) return;
      const angleSpan = (n / total) * Math.PI * 2;
      const endAngle = cursorAngle + angleSpan;
      const color = getMarkerFillColor(bucket);
      // Edge case: 1 unico bucket com todos os parques — full circle.
      // SVG arc nao desenha bem 360°, entao usamos circle nesse caso.
      if (n === total) {
        pieSvg += `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${color}"/>`;
      } else {
        const d = buildPieSliceD(cx, cy, r, cursorAngle, endAngle);
        pieSvg += `<path d="${d}" fill="${color}"/>`;
      }
      cursorAngle = endAngle;
    });
  }

  // Pill de soma abaixo do circulo: icone + valor por segmento
  // (zap laranja = geracao TGA, gota azul = potencia bomba). Mesma
  // renderizacao do pill standalone (renderPillSegments) pra ficarem
  // visualmente identicos — so os icones distinguem os tipos, sem texto.
  let pillSvg = '';
  if (hasPill) {
    const pillX = (W - pillW) / 2;
    const pillY = clusterSize + pillGap;
    pillSvg = `
      <rect x="${pillX}" y="${pillY}" width="${pillW}" height="${pillH}" rx="10"
        fill="rgba(0,0,0,0.72)" stroke="${MARKER_OUTLINE_COLOR}" stroke-width="1"/>
      ${renderPillSegments(pillSegs, pillX, pillW, pillY)}
    `;
  }

  // Badge vermelho com o Nº de parques EM ALARME no cluster, no canto sup.
  // direito do circulo. Da o numero exato (a fatia vermelha so da a
  // proporcao). So aparece quando ha alarme no agrupamento.
  let alarmBadgeSvg = '';
  const alarmCount = counts['alarme/bloqueio'] || 0;
  if (alarmCount > 0) {
    const bx = cx + r * 0.72;
    const by = cy - r * 0.72;
    const br = Math.round(9 * sizeScale);
    const bLabel = alarmCount > 9 ? '9+' : `${alarmCount}`;
    const bFont = Math.round((bLabel.length > 1 ? 9 : 11) * sizeScale);
    alarmBadgeSvg = `
      <circle cx="${bx}" cy="${by}" r="${br}" fill="${themes.red}" stroke="#fff" stroke-width="1.5"/>
      <text x="${bx}" y="${by}" fill="#fff" font-size="${bFont}" font-weight="800" font-family="Arial, sans-serif" text-anchor="middle" dominant-baseline="central">${bLabel}</text>
    `;
  }

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
      <defs>
        <filter id="cluster-shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#000000" flood-opacity="0.45"/>
        </filter>
      </defs>
      <g filter="url(#cluster-shadow)">
        ${pieSvg}
        <!-- Borda externa amarela (mesma do marker) une visualmente
             clusters e markers como pertencentes ao mesmo sistema -->
        <circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${MARKER_OUTLINE_COLOR}" stroke-width="3"/>
        <!-- Disco escuro no centro pra contrastar o count vs as fatias -->
        <circle cx="${cx}" cy="${cy}" r="${Math.round(12 * sizeScale)}" fill="rgba(0,0,0,0.55)"/>
        <text x="${cx}" y="${cy}" fill="${themes.white}" font-size="${fontSize}" font-weight="800" font-family="Arial, sans-serif" text-anchor="middle" dominant-baseline="central">${escapeXmlText(label)}</text>
        ${alarmBadgeSvg}
        ${pillSvg}
      </g>
    </svg>
  `;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(W, H),
    // anchor no centro do CIRCULO (nao do svg) pra ficar na posicao geo.
    anchor: new google.maps.Point(cx, cy),
  };
};

// Cache de cluster icons. Sem isso, re-criava objeto a cada re-cluster,
// gerando flicker quando o usuario pana o mapa. Chave inclui a
// distribuicao serializada de status pra cobrir clusters com mesma
// proporcao porem composicao distinta nao gerarem mesmo cache (raro).
const clusterIconCache = new Map();
const CLUSTER_CACHE_MAX_SIZE = 200;
export const getCachedClusterIcon = (google, counts, total, sums) => {
  if (!google || !google.maps) return undefined;
  const cappedTotal = total > 99 ? '99+' : `${total}`;
  // Soma entra na chave (arredondada) pra o icone atualizar quando o
  // total tempo-real muda — senao o cache devolveria o pill velho.
  const genKey = sums && Number.isFinite(sums.gen) ? Math.round(sums.gen) : 0;
  const consKey = sums && Number.isFinite(sums.cons) ? Math.round(sums.cons) : 0;
  // Chave: a-b-c-d-e|99+|gen|cons
  const key = `${counts['alarme/bloqueio'] || 0}-${counts['atenção'] || 0}-${counts.rodando || 0}-${counts.desligada || 0}-${counts['sem dados'] || 0}|${cappedTotal}|${genKey}|${consKey}`;
  const cached = clusterIconCache.get(key);
  if (cached) return cached;
  const icon = buildClusterIcon(google, counts, total, sums);
  if (clusterIconCache.size >= CLUSTER_CACHE_MAX_SIZE) {
    const firstKey = clusterIconCache.keys().next().value;
    clusterIconCache.delete(firstKey);
  }
  clusterIconCache.set(key, icon);
  return icon;
};
